import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, Save, Shield } from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../foundation/altzero_base/base/components/ui/select';
import { organisationService } from '../services/organisationService';
import { isClientShielded } from '../utils/shieldPlatinum';
import type { BvOrganisationFormData, BvClientStatus } from '../types/billValidationTypes';

const EMPTY: BvOrganisationFormData = {
  name: '', abn: '', sf_account_id: '', status: 'active',
  parent_org_id: '', google_drive_link: '', broker_id: '',
  // Job 10: a Bill Validation client is Shield Platinum by definition. The form
  // no longer offers a choice; the service sets the flag on insert regardless.
  shield_platinum: true,
};

export function OrganisationFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState<BvOrganisationFormData>(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    organisationService.getById(id).then((r) => {
      if (r.success && r.data) {
        const o = r.data;
        const bv = o.extn?.bv;
        setForm({
          name: o.name,
          abn: o.abn ?? '',
          sf_account_id: o.sf_account_id ?? '',
          status: o.status,
          parent_org_id: bv?.parent_org_id ?? '',
          google_drive_link: bv?.google_drive_link ?? '',
          broker_id: bv?.broker_id ?? '',
          // Never sent as false — update() only ever promotes the flag.
          shield_platinum: isClientShielded(o),
        });
      } else {
        setError(r.error ?? 'Failed to load entity');
      }
      setLoading(false);
    });
  }, [isEdit, id]);

  const set = <K extends keyof BvOrganisationFormData>(k: K, v: BvOrganisationFormData[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Entity name is required'); return; }
    setSaving(true);
    setError(null);
    const r = isEdit && id
      ? await organisationService.update(id, form)
      : await organisationService.create(form);
    if (!r.success || !r.data) {
      setError(r.error ?? 'Save failed');
      setSaving(false);
      return;
    }
    navigate(`/bill-validation/organisations/${r.data.id}`);
  };

  if (loading) {
    return (
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-4">
        <div className="h-6 w-40 bg-gray-100 animate-pulse rounded" />
        <div className="h-96 bg-gray-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate(isEdit && id ? `/bill-validation/organisations/${id}` : '/bill-validation/organisations')}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {isEdit ? 'Back to Entity' : 'Back to Entities'}
      </button>

      <div className="max-w-4xl space-y-6">

        {/* Header card */}
        <Card>
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="bg-primary/10 p-3 rounded-lg flex-shrink-0">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl md:text-3xl">
                  {isEdit ? 'Edit Entity' : 'New Entity'}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {isEdit
                    ? 'Update entity details and BV configuration'
                    : 'Create an active or onboarding bill-validation customer'}
                </p>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Basic Info */}
        <Card>
          <CardHeader><CardTitle className="text-lg">Basic Information</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="org-name">Entity Name <span className="text-red-500">*</span></Label>
                <Input
                  id="org-name"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="e.g. Acme Australia Pty Ltd"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="org-abn">ABN</Label>
                <Input
                  id="org-abn"
                  value={form.abn ?? ''}
                  onChange={(e) => set('abn', e.target.value)}
                  placeholder="XX XXX XXX XXX"
                  className="mt-1.5 font-mono"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label>Status <span className="text-red-500">*</span></Label>
                <Select value={form.status} onValueChange={(v) => set('status', v as BvClientStatus)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Active = currently being bill-validated. Onboarding = setup in progress.
                </p>
              </div>
              <div>
                <Label htmlFor="org-drive">Google Drive Link</Label>
                <Input
                  id="org-drive"
                  value={form.google_drive_link ?? ''}
                  onChange={(e) => set('google_drive_link', e.target.value)}
                  placeholder="https://drive.google.com/…"
                  className="mt-1.5"
                />
              </div>
            </div>

            {/* Manual Salesforce link — lets an entity be matched to SF without
                waiting on an import to fill it in. */}
            <div>
              <Label htmlFor="org-sf-id">Salesforce Account ID</Label>
              <Input
                id="org-sf-id"
                value={form.sf_account_id ?? ''}
                onChange={(e) => set('sf_account_id', e.target.value.trim())}
                placeholder="e.g. 0011x00000ABCDEAA1"
                className="mt-1.5 font-mono"
              />
            </div>
          </CardContent>
        </Card>

        {/* BV Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              BV Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="org-parent">Parent Entity ID</Label>
                <Input
                  id="org-parent"
                  value={form.parent_org_id ?? ''}
                  onChange={(e) => set('parent_org_id', e.target.value)}
                  placeholder="UUID of parent org (optional)"
                  className="mt-1.5 font-mono text-xs"
                />
              </div>
              <div>
                <Label htmlFor="org-broker">Broker ID</Label>
                <Input
                  id="org-broker"
                  value={form.broker_id ?? ''}
                  onChange={(e) => set('broker_id', e.target.value)}
                  placeholder="Assigned broker UUID (optional)"
                  className="mt-1.5 font-mono text-xs"
                />
              </div>
            </div>

            {/* Job 10: not a choice any more — every client in Bill Validation is
                a Shield Platinum client, so it's stated rather than asked. */}
            <div className="flex items-center gap-2.5 rounded-lg border border-blue-200 bg-blue-50/60 px-4 py-3">
              <Shield className="h-4 w-4 text-blue-600 shrink-0" />
              <div>
                <p className="text-sm font-medium text-gray-900">Shield Platinum</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Applied automatically — every client in Bill Validation is Shield Platinum.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Errors */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pb-8">
          <Button
            variant="outline"
            onClick={() => navigate(isEdit && id ? `/bill-validation/organisations/${id}` : '/bill-validation/organisations')}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Entity'}
          </Button>
        </div>

      </div>
    </div>
  );
}
